import React, { FC, useEffect } from 'react';
import { useFetchGames } from '../../../hooks/useFetchGames';
import { Game } from '../../../components/Games/games.model';
import { transformGameData } from '../../../utils/games/games.utils';
import { useUserContext } from '../../../providers/UserProvider';
import { getMiniGamePlayUrl } from '../../../components/Games/helper';
import { useMinigamesAuth } from './useMinigamesAuth';
import { GameTile } from './components/GameTile';
import { PromoBanner } from './components/PromoBanner';
import { MinigamesSkeleton } from './components/MinigamesSkeleton';
import './MinigamesPage.scss';

export const MinigamesPage: FC = () => {
  const { isUserAuthenticated, isAuthLoading } = useMinigamesAuth();
  const { userProfileId } = useUserContext();

  const {
    fetchGamesByCategory,
    fetchedGamesByCategoryData,
    isFetchedGamesByCategoryDataLoading,
    fetchedGamesByCategoryError,
  } = useFetchGames({
    category: 'mini-game-itv',
    count: 100,
  });

  useEffect(() => {
    window.zE('messenger', 'hide');

    return () => {
      window.zE('messenger', 'show');
    };
  }, []);

  useEffect(() => {
    if (isUserAuthenticated && !fetchedGamesByCategoryData) {
      fetchGamesByCategory();
    }
  }, [isUserAuthenticated, fetchedGamesByCategoryData]);

  const handleGameClick = async (game: Game): Promise<void> => {
    if (!userProfileId) return;

    try {
      const miniGameUrl = getMiniGamePlayUrl(game);

      (window.parent || window).postMessage(JSON.stringify({ type: 'OPEN_SLOT', link: miniGameUrl }), '*');

      if (window.top && window.top !== window) {
        window.top.postMessage(JSON.stringify({ type: 'gameClicked', gameId: game.gameId }), '*');
      }
    } catch (error) {
      console.error('Failed to launch game:', error);
    }
  };

  if (isAuthLoading || !isUserAuthenticated) {
    return null;
  }

  if (isFetchedGamesByCategoryDataLoading) {
    return <MinigamesSkeleton />;
  }

  if (fetchedGamesByCategoryError) {
    return (
      <div className="minigames-page">
        <div className="main">
          <div className="mini-games error">Error loading minigames. Please try again.</div>
        </div>
      </div>
    );
  }

  const categories = fetchedGamesByCategoryData?.categories || [];
  const rawGames = categories[0]?.games || [];
  const staticBanner = categories[0]?.static_banner;
  const games = rawGames.map((item) => transformGameData(item?.game_id)).filter(Boolean);

  const heroGame = games[0];
  const topGames = games.slice(1, 5);
  const remainingGames = games.slice(5);

  return (
    <div className="minigames-page">
      <div className="main">
        {games.length > 0 && (
          <div className="mini-games-top">
            {heroGame && <GameTile game={heroGame} className="mini-games-top__hero" onClick={handleGameClick} />}
            {topGames.map(
              (game) =>
                game && (
                  <GameTile key={game.id} game={game} className="mini-games-top__tile" onClick={handleGameClick} />
                )
            )}
            {staticBanner && <PromoBanner staticBanner={staticBanner} />}
          </div>
        )}
        {remainingGames.length > 0 && (
          <div className="mini-games-remaining">
            {remainingGames.map(
              (game) =>
                game && (
                  <GameTile
                    key={game.id}
                    game={game}
                    className="mini-games-remaining__tile"
                    onClick={handleGameClick}
                  />
                )
            )}
          </div>
        )}
      </div>
    </div>
  );
};
